import { useCallback, useState } from "react";
import { Urls } from "../Urls";
import axios from "../Axios/Axios";
const BASE_URL = `${Urls.spot_dashboard}`;


export const VIEW_TYPES = {
  MY_ENQUIRIES: "my_enquiries",
  COMPANY_ENQUIRIES: "company_enquiries",
};

export const LOCATION_FILTER_TYPES = {
  ORIGIN: "origin",
  LANE: "lane",
};

const DEFAULT_DASHBOARD_DATA = {
  cards: {
    spot_enq_requested: 0,
    spot_enq_approved: 0,
    spot_enq_approved_percentage: 0,
    truckload_requested: 0,
    spend: 0,
    spend_in_lakhs: 0,
    accepted_volume: 0,
    accepted_percentage: 0,
    savings: 0,
    saving_in_lakhs: 0,
    saving_percentage: 0,
    avg_response_time_hours: 0,
  },
  charts: {
    spend_by_lane_origin_top_5: [],
    spot_enquiries_by_user: [],
    count_of_enquiries_by_month: [],
    spend_distribution_by_transporter: [],
    savings_by_transporter: [],
    spend_by_lane_origin_filtered: [],
  },
};

const isValidViewType = viewType =>
  [VIEW_TYPES.MY_ENQUIRIES, VIEW_TYPES.COMPANY_ENQUIRIES].includes(viewType);

const getMonthDifference = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  return (
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth()) +
    (end.getDate() >= start.getDate() ? 0 : -1)
  );
};

const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    throw new Error("Please select both start date and end date");
  }

  if (new Date(startDate) > new Date(endDate)) {
    throw new Error("Start date cannot be after end date");
  }

  if (getMonthDifference(startDate, endDate) > 3) {
    throw new Error("Date range cannot be more than 3 months");
  }
};

const useSpotDash2 = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [dashboardData, setDashboardData] = useState(DEFAULT_DASHBOARD_DATA);
  const [filters, setFilters] = useState(null);

  const [userList, setUserList] = useState([]);
  const [originList, setOriginList] = useState([]);
  const [laneList, setLaneList] = useState([]);

  const [spotResponse, setSpotResponse] = useState(null);
  const [dateResponse, setDateResponse] = useState(null);
  const [locationResponse, setLocationResponse] = useState(null);

  const handleApiResponse = useCallback(data => {
    if (!data?.success) {
      throw new Error(data?.message || "Something went wrong");
    }

    setDashboardData(data.dashboard_data || DEFAULT_DASHBOARD_DATA);
    setFilters(data.filters || null);
    setUserList(Array.isArray(data.user_list) ? data.user_list : []);
    setOriginList(Array.isArray(data.origin_list) ? data.origin_list : []);
    setLaneList(Array.isArray(data.lane_list) ? data.lane_list : []);

    return data;
  }, []);

  const callApi = useCallback(
    async ({ endpoint, payload, responseType }) => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.post(`${BASE_URL}${endpoint}`, payload);
        const data = handleApiResponse(response.data);

        if (responseType === "spot") setSpotResponse(data);
        if (responseType === "date") setDateResponse(data);
        if (responseType === "location") setLocationResponse(data);

        return data;
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Unable to fetch spot dashboard data";

        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [handleApiResponse]
  );

  const fetchInitialDashboard = useCallback(
    async ({ viewType = VIEW_TYPES.MY_ENQUIRIES } = {}) => {
      if (!isValidViewType(viewType)) {
        setError("Invalid view_type. Allowed values are my_enquiries and company_enquiries");
        return null;
      }

      return callApi({
        endpoint: "/spot",
        payload: {
          view_type: viewType,
        },
        responseType: "spot",
      });
    },
    [callApi]
  );

  const fetchDashboardByDate = useCallback(
    async ({
      viewType = VIEW_TYPES.MY_ENQUIRIES,
      startDate,
      endDate,
      selectedUsers = [],
    }) => {
      if (!isValidViewType(viewType)) {
        setError("Invalid view_type. Allowed values are my_enquiries and company_enquiries");
        return null;
      }

      try {
        validateDateRange(startDate, endDate);
      } catch (err) {
        setError(err.message);
        return null;
      }

      const payload = {
        view_type: viewType,
        start_date: startDate,
        end_date: endDate,
      };

      if (viewType === VIEW_TYPES.COMPANY_ENQUIRIES) {
        payload.selected_users = selectedUsers;
      }

      return callApi({
        endpoint: "/dates",
        payload,
        responseType: "date",
      });
    },
    [callApi]
  );

  const fetchLocationData = useCallback(
    async ({
      viewType = VIEW_TYPES.MY_ENQUIRIES,
      startDate,
      endDate,
      selectedUsers = [],
      filterType,
      origin = "",
      destination = "",
    }) => {
      if (!isValidViewType(viewType)) {
        setError("Invalid view_type. Allowed values are my_enquiries and company_enquiries");
        return null;
      }

      try {
        validateDateRange(startDate, endDate);
      } catch (err) {
        setError(err.message);
        return null;
      }

      if (filterType === LOCATION_FILTER_TYPES.ORIGIN && !origin) {
        setError("origin is required when filter_type is origin");
        return null;
      }

      if (filterType === LOCATION_FILTER_TYPES.LANE && (!origin || !destination)) {
        setError("origin and destination are required when filter_type is lane");
        return null;
      }

      if (![LOCATION_FILTER_TYPES.ORIGIN, LOCATION_FILTER_TYPES.LANE].includes(filterType)) {
        setError("Invalid location filter_type");
        return null;
      }

      const payload = {
        view_type: viewType,
        start_date: startDate,
        end_date: endDate,
        selected_users: selectedUsers,
        filter_type: filterType,
        origin,
      };

      if (filterType === LOCATION_FILTER_TYPES.LANE) {
        payload.destination = destination;
      }

      return callApi({
        endpoint: "/locations",
        payload,
        responseType: "location",
      });
    },
    [callApi]
  );

  const clearError = useCallback(() => {
    setError("");
  }, []);

  const resetDashboardState = useCallback(() => {
    setDashboardData(DEFAULT_DASHBOARD_DATA);
    setFilters(null);
    setUserList([]);
    setOriginList([]);
    setLaneList([]);
    setSpotResponse(null);
    setDateResponse(null);
    setLocationResponse(null);
    setError("");
  }, []);

  return {
    loading,
    error,
    clearError,
    resetDashboardState,

    dashboardData,
    filters,
    userList,
    originList,
    laneList,

    spotResponse,
    dateResponse,
    locationResponse,

    fetchInitialDashboard,
    fetchDashboardByDate,
    fetchLocationData,

    VIEW_TYPES,
    LOCATION_FILTER_TYPES,
  };
};

export default useSpotDash2;